import { motion } from "framer-motion";
import { ArrowUpRight, ShieldAlert } from "lucide-react";
import ImportanceBadge from "./ImportanceBadge";
import ShareBar from "./ShareBar";
import { fmtDate } from "../../lib/intelApi";
import { SkeletonCards, ErrorState, EmptyState } from "./IntelStates";

const STATS = [
  { key: "total", label: "Total updates" },
  { key: "security", label: "Security-related" },
  { key: "iam", label: "IAM-related" },
  { key: "breaking", label: "Breaking / deprecations", accent: true },
  { key: "actions", label: "Recommended actions", accent: true },
];

export default function WeeklyBrief({ query }) {
  const { data, isLoading, isError, refetch } = query;
  return (
    <section data-testid="weekly-brief" className="space-y-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-accent">Weekly intelligence</div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tighter leading-[0.95] uppercase">
            This Week in
            <br />
            <span className="text-accent">Cloud Security</span>.
          </h2>
        </div>
        {data && (
          <div className="flex flex-col items-start md:items-end gap-3">
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              {fmtDate(data.window.from)} → {fmtDate(data.window.to)}
            </div>
            {data.share_week && <ShareBar week={data.share_week} showArchive />}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border border-border" data-testid="weekly-stats">
        {STATS.map((s, i) => (
          <div key={s.key} className={`bg-background p-5 md:p-6 ${i === 4 ? "col-span-2 md:col-span-1" : ""}`} data-testid={`weekly-stat-${s.key}`}>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{s.label}</div>
            <div className={`mt-2 font-heading font-black text-3xl md:text-4xl tracking-tighter tabular-nums ${s.accent ? "text-accent" : ""}`}>
              {data ? data.stats[s.key] : <span className="inline-block w-10 h-8 bg-muted animate-pulse" />}
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center gap-3 mb-6">
          <ShieldAlert size={14} className="text-accent" />
          <span className="font-mono text-[11px] uppercase tracking-[0.25em]">Top 5 things I should know this week</span>
          <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            · ranked by importance × relevance, not recency
          </span>
        </div>
        {isLoading && <SkeletonCards count={2} />}
        {isError && <ErrorState onRetry={refetch} />}
        {data && data.top.length === 0 && (
          <EmptyState title="Quiet week" body="No processed updates in the last 7 days yet. The ingestion job runs every 6 hours." />
        )}
        {data && data.top.length > 0 && (
          <ol className="border-t border-border" data-testid="weekly-top5">
            {data.top.map((item, i) => (
              <motion.li
                key={item.id}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                className="group grid grid-cols-[auto_1fr] md:grid-cols-[80px_1fr_auto] gap-x-5 md:gap-x-8 gap-y-3 py-6 md:py-8 border-b border-border"
                data-testid={`weekly-top-${i + 1}`}
              >
                <div className="font-heading font-black text-3xl md:text-5xl tracking-tighter text-accent leading-none tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <ImportanceBadge level={item.importance} />
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                      {item.technology} · {item.category} · {fmtDate(item.published_at)}
                    </span>
                  </div>
                  <h3 className="mt-3 font-heading font-black text-lg md:text-2xl tracking-tight leading-tight group-hover:text-accent transition-colors">
                    <a href={item.url} target="_blank" rel="noopener noreferrer">
                      {item.title}
                    </a>
                  </h3>
                  <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-foreground/85 max-w-[72ch]">{item.ai_summary}</p>
                  {item.why_it_matters_to_me && (
                    <p className="mt-3 text-sm leading-relaxed text-foreground/70 border-l-2 border-accent pl-4 max-w-[72ch]">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent mr-2">For me</span>
                      {item.why_it_matters_to_me}
                    </p>
                  )}
                  {item.recommendation && (
                    <p className="mt-3 text-sm text-muted-foreground max-w-[72ch]">
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] mr-2">Action</span>
                      {item.recommendation}
                    </p>
                  )}
                </div>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="col-start-2 md:col-start-3 self-start inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] border border-border px-4 py-2.5 hover:border-accent hover:text-accent transition-colors"
                >
                  Source <ArrowUpRight size={13} />
                </a>
              </motion.li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}
